import { getQueryKey } from "@trpc/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
  beginIdentityScopedPrefetch,
  type IdentityScopedPrefetchGuard,
} from "./prefetchIdentityGuard";
import { trpc, trpcVanilla } from "@client/trpc";
import {
  corpusQueryPersister,
  setPersistedQueryUserScope,
} from "@client/utils/queryPersister";
import { CORPUS_STALE_TIME_MS } from "@lib/corpusFreshness";

// True when the URL lands anywhere in the funder workspace — every funder page
// (review rail, proposals, recommenders) mounts the corpus queries.
export function workspaceCorpusPathTargeted(
  roundId: string,
  pathname: string,
): boolean {
  return pathname.includes(`/round/${roundId}/funder`);
}

/**
 * Resolve the effective viewer for a corpus-keyed prefetch and scope the
 * persisted cache to them; shared by every prefetch that warms a
 * `clientQueryScope` query key (the corpus pair below, the Tracks rail's
 * track summary).
 *
 * trpcVanilla builds impersonation headers from the LIVE ref at request time,
 * while these prefetches bake the effective user into the cache key — so an
 * impersonation switch while a prefetch is in flight could cache one
 * identity's corpus under another's key (exactly the leak the
 * clientQueryScope design exists to prevent). The shared guard captures the
 * viewer up front and drops whatever the chain warmed if it moves; the null
 * return here lets callers skip their fetches entirely when the switch lands
 * before they fire. (A late persister write is already inert: the storage key
 * uses the CURRENT global scope, so a mismatched write lands under a
 * scope+input pair no hook ever reads.)
 *
 * Scopes persisted-cache access to the effective user BEFORE the persister
 * touches storage (unscoped access is a no-op in both directions). The corpus
 * hooks set the same scope in render (useCorpusQueryInput); this runs earlier
 * than any render.
 */
export async function resolveWorkspacePrefetchViewer(
  queryClient: QueryClient,
  guard: IdentityScopedPrefetchGuard,
): Promise<string | null> {
  const effectiveUserId =
    guard.startedAs ??
    (
      await queryClient.fetchQuery({
        queryKey: getQueryKey(trpc.getCurrentUser, undefined, "query"),
        queryFn: () => trpcVanilla.getCurrentUser.query(),
        staleTime: 5 * 60 * 1000,
      })
    ).userId;
  if (effectiveUserId == null) return null;
  if (guard.viewerChanged()) return null;
  setPersistedQueryUserScope(effectiveUserId);
  return effectiveUserId;
}

// Warm getFunderWorkspaceEnrichments + getFunderSummaries as soon as the
// effective user is known, instead of after the bootstrap, the field-responses
// fetch, and the aggregate render have all completed and the workspace's
// useQuery hooks finally mount. On the live round that mount-gated chain held
// the pair back ~500ms, and the pair itself is the slowest call of the load
// (~630ms server-side, measured 2026-07-16) — so its response, which fills the
// workspace's weights/summaries, landed ~1.4s in. Fired here it rides alongside
// the bootstrap and lands before the shell first paints.
//
// Same technique as prefetchWorkspaceMeetings: fetch with the exact query key
// the hooks use, so their useQuery dedupes onto this fetch. The corpus key
// includes `clientQueryScope` — the EFFECTIVE user (impersonation-aware; see
// useCorpusQueryInput) — which is why this resolves the impersonation ref and
// falls back to getCurrentUser (the same fetchQuery key/staleTime the
// useCurrentUser hook and loadRoundData share, so no extra request fires).
//
// The prefetches run through `corpusQueryPersister` — the same persister the
// mounted hooks attach — with the user scope set first (the persister no-ops
// while the scope is unknown). This keeps the offline story intact: a
// prefetch-served load still writes the fresh corpus back to IndexedDB, and on
// a warm cache the persister restores the stored corpus instantly and then
// refetches (stale under the 60s staleTime), so the early network fetch still
// happens. Without the persister, a prefetched load would never write the
// session's corpus to the persisted cache and an offline reload would serve
// the previous session's data.
//
// Both prefetches go through trpcVanilla's UNBATCHED httpLink, so each is a
// solo GET the server's pre-encoded brotli byte lane can answer
// (server/api/corpusBytes.ts). The mounted hooks ride the same unbatched lane
// (CRITICAL_TRPC_OPTIONS at their call sites), so whichever side wins the
// startup race, the request shape is the same.
//
// Known, accepted waste: a round admin who is NOT a funder (nor SFC admin)
// opening a /funder URL is redirected away by FunderRouteGuard before the
// workspace mounts — but this prefetch fires first and the server authorizes
// round admins for these routes, so that rare visit downloads a corpus the UI
// never shows. Deciding funder-ness requires the round data itself, i.e. the
// very wait this prefetch exists to get ahead of, so the trade is deliberate.
export function prefetchWorkspaceCorpus(
  queryClient: QueryClient,
  roundId: string,
  pathname: string,
): void {
  if (!workspaceCorpusPathTargeted(roundId, pathname)) return;
  void (async () => {
    const guard = beginIdentityScopedPrefetch(queryClient);
    try {
      const effectiveUserId = await resolveWorkspacePrefetchViewer(
        queryClient,
        guard,
      );
      if (effectiveUserId == null) return;

      const input = { roundId, clientQueryScope: effectiveUserId };
      const enrichmentsKey = guard.warm(
        getQueryKey(trpc.getFunderWorkspaceEnrichments, input, "query"),
      );
      const summariesKey = guard.warm(
        getQueryKey(trpc.getFunderSummaries, input, "query"),
      );
      // Matches CORPUS_QUERY_OPTIONS' staleTime so the mounting hooks treat the
      // prefetched entries as fresh rather than refetching them.
      const staleTime = CORPUS_STALE_TIME_MS;
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: enrichmentsKey,
          queryFn: () => trpcVanilla.getFunderWorkspaceEnrichments.query(input),
          staleTime,
          persister: corpusQueryPersister,
        }),
        queryClient.prefetchQuery({
          queryKey: summariesKey,
          queryFn: () => trpcVanilla.getFunderSummaries.query(input),
          staleTime,
          persister: corpusQueryPersister,
        }),
      ]);
      // Switched mid-flight: the responses may have been fetched with the new
      // identity's headers. Drop them rather than let the old identity's key
      // serve them.
      guard.discardIfViewerChanged();
    } catch {
      // This is an optimization only. The mounted hooks retain their normal
      // loading and error behavior if the early requests fail.
    }
  })();
}
